import type {
  MaskitoOptions,
  MaskitoPreprocessor,
} from "@maskito/core"
import { maskitoDate } from "@maskito/kit"
import type { MaskitoDateMode } from "@maskito/kit"
import { useMaskito } from "@maskito/react"
import {
  LargeInput,
  LargeInputIconButton,
  LargeInputWithIconButton,
  ValidationContainer,
  ValidationMessage,
  Dialog,
  DialogButton,
  DialogCloseX,
  DialogContent,
  DialogDescription,
  DialogHeading,
  CalendarOutlineIcon,
  UnstyledInput,
} from "@tandemdiabetes/sugarmate-react-components"
import dayjs from "dayjs"
import {
  useEffect,
  useMemo,
  useCallback,
  useRef,
  useState,
} from "react"
import type {
  ChangeEventHandler,
  FocusEventHandler,
  FormEventHandler,
  KeyboardEventHandler,
  MouseEventHandler,
} from "react"

import styles from "../AccountForms.module.scss"
import customStyles from "./PersonalInformationForm.module.scss"
import { RetriggerValidation } from "./RetriggerValidation"

import {
  usePolyglot,
  useCountrySelectionContext,
} from "src/contexts"
import {
  useCountries,
  useToday,
} from "src/hooks"
import {
  getLocaleDateFormat,
  getLocaleDateStringFromDate,
  getDateFromLocaleDateString,
} from "src/utils"

const polyglotPrefix = "components.account_forms.personal_information.date_of_birth."
const dateOfBirthId = "date-of-birth-id"

const dateOfBirthErrorId = "date-of-birth-error-id"
const dateInputFormat = "YYYY-MM-DD"

interface Props {
  value: string;
  onChange: (v: string) => void;
}

export function DateOfBirthInput(props: Props) {
  const {
    value,
    onChange: propsOnChange,
  } = props

  const polyglot = usePolyglot()
  const hadValueOnFirstRender = useRef<boolean>(!!value)
  const countries = useCountries().data
  const today = useToday()

  const [
    isDialogOpen,
    setIsDialogOpen,
  ] = useState<boolean>(false)

  const { countryCode } = useCountrySelectionContext()

  const minimumAge = useMemo(
    () => {
      const currentCountry = (countries ?? []).find(
        (country) => country.country_code === countryCode,
      )

      if (currentCountry) {
        return currentCountry.minimum_age
      }

      return 18
    },
    [
      countries,
      countryCode,
    ],
  )

  const latestValidDateOfBirth = useMemo(
    () => dayjs(today)
      .subtract(
        minimumAge,
        "years",
      )
      .startOf("day"),
    [
      today,
      minimumAge,
    ],
  )

  const earliestValidDateOfBirth = useMemo(
    () => dayjs(today)
      .subtract(
        150,
        "years",
      )
      .startOf("day"),
    [today],
  )

  // hasBlurred will be true if the user has blurred the input at least once
  const [
    hasBlurred,
    setHasBlurred,
  ] = useState<boolean>(false)

  const inputRef = useRef<HTMLInputElement | null>(null)

  const [
    customError,
    setCustomError,
  ] = useState("")

  const inputPattern = useMemo(
    () => getLocaleDateFormat({
      language: polyglot.locale(),
      dayAbbreviation: "\\d",
      monthAbbreviation: "\\d",
      yearAbbreviation: "\\d",
      escapeSeparatorRegexp: true,
    }).dateFormat,
    [polyglot],
  )

  const updateCustomError = useCallback(
    (error: string) => {
      inputRef.current?.setCustomValidity(error)
      setCustomError(error)
    },
    [
      setCustomError,
      inputRef,
    ],
  )

  const validateAge = useCallback(
    (userInput: string) => {
      const newValue = getDateFromLocaleDateString(
        polyglot.locale(),
        userInput,
      )
      const isValidDate = !isNaN(newValue.valueOf())

      const inputRegex = new RegExp(inputPattern)
      const inputMatchesPattern = inputRegex.test(userInput)

      if (isValidDate) {
        const isTooYoung = dayjs(newValue)
          .startOf("day")
          .isAfter(latestValidDateOfBirth)

        const isTooOld = dayjs(newValue)
          .startOf("day")
          .isBefore(earliestValidDateOfBirth)

        const isInFuture = dayjs(newValue).isAfter(today)

        if (isTooOld || isInFuture) {
          updateCustomError(polyglot.t(`${polyglotPrefix}invalid_error`))
        } else if (isTooYoung) {
          updateCustomError(polyglot.t(
            `${polyglotPrefix}too_young_error`,
            minimumAge,
          ))
        } else {
          updateCustomError("")
        }
      } else if (inputMatchesPattern) {
        updateCustomError(polyglot.t(`${polyglotPrefix}invalid_error`))
      } else {
        updateCustomError("")
      }
    },
    [
      minimumAge,
      latestValidDateOfBirth,
      earliestValidDateOfBirth,
      today,
      inputPattern,
      polyglot,
      updateCustomError,
    ],
  )

  const {
    dateFormat: maskitoDateMode,
    dateSeparators,
  } = useMemo(
    () => {
      const { dateFormat } = getLocaleDateFormat({
        language: polyglot.locale(),
        dayAbbreviation: "d",
        monthAbbreviation: "m",
        yearAbbreviation: "y",
      })
      const separators = dateFormat.match(/[^dmy]+/g) ?? []

      return {
        dateFormat: dateFormat.replace(
          /[^dmy]+/g,
          "/",
        ) as MaskitoDateMode,
        dateSeparators: separators,
      }
    },
    [polyglot],
  )

  const dateSeparator = dateSeparators[0] ?? "/"

  /*
   * Maskito normally waits for a two-digit field to be complete before moving
   * past it. When a user explicitly enters a locale separator after one digit,
   * pad that field and advance to the next one instead. Keeping this in the
   * Maskito preprocessor pipeline lets Maskito retain ownership of its mask
   * state and selection handling for keyboard, paste, and native input events.
   *
   * The browser can report the separator either as incoming `data` or as part
   * of the element value already applied before the processor runs, so both
   * forms are recognised below.
   */
  const typedSeparatorPreprocessor = useMemo<MaskitoPreprocessor>(
    () => (
      {
        elementState,
        data,
      },
      actionType,
    ) => {
      const {
        value: currentValue,
        selection: [
          from,
          to,
        ],
      } = elementState
      // Match the complete locale separator, including multi-character forms
      // such as ". ", when the browser has inserted it before this processor.
      const separatorAlreadyInserted = currentValue.lastIndexOf(
        dateSeparator,
        from,
      ) === from - dateSeparator.length
      const isTypedSeparator = actionType === "insert"
        && (
          separatorAlreadyInserted
          || (
            data.length === 1
            && data.trim().length > 0
            && dateSeparator.includes(data)
          )
        )
        && from === to
      const fieldEnd = separatorAlreadyInserted
        ? from - dateSeparator.length
        : from
      const fieldStart = currentValue.lastIndexOf(
        dateSeparator,
        fieldEnd - 1,
      ) + dateSeparator.length
      const currentField = currentValue.slice(
        fieldStart,
        fieldEnd,
      )
      // Insert the full locale separator, not only the key that triggered it.
      const separatorToInsert = separatorAlreadyInserted
        ? ""
        : dateSeparator

      if (!isTypedSeparator || !/^\d$/.test(currentField)) {
        return { elementState }
      }

      const paddedValue = [
        currentValue.slice(
          0,
          fieldStart,
        ),
        `0${currentValue.slice(
          fieldStart,
          fieldEnd,
        )}`,
        separatorToInsert,
        currentValue.slice(fieldEnd),
      ].join("")
      // Advance past the added zero and, when applicable, every character of a newly inserted multi-character separator.
      const caretPosition = from + 1 + (
        separatorAlreadyInserted
          ? 0
          : dateSeparator.length
      )

      return {
        elementState: {
          value: paddedValue,
          selection: [
            caretPosition,
            caretPosition,
          ],
        },
        data: "",
      }
    },
    [dateSeparator],
  )

  const maskitoOptions = useMemo<MaskitoOptions>(
    () => {
      const dateOptions = maskitoDate({
        mode: maskitoDateMode,
        separator: dateSeparator,
      })

      return {
        ...dateOptions,
        preprocessors: [
          typedSeparatorPreprocessor,
          ...(dateOptions.preprocessors ?? []),
        ],
      }
    },
    [
      maskitoDateMode,
      dateSeparator,
      typedSeparatorPreprocessor,
    ],
  )

  const maskitoRef = useMaskito({ options: maskitoOptions })

  // We use a callback because one element must be registered with both LargeInput.ref and Maskito.maskitoRef
  // An object ref can only be assigned to one ref target directly.
  const setTextInputRef = useCallback(
    (element: HTMLInputElement | null) => {
      inputRef.current = element
      maskitoRef(element)
    },
    [maskitoRef],
  )

  const onDateTextInput: FormEventHandler<HTMLInputElement> = useCallback(
    (inputEvent) => {
      const newValue = inputEvent.currentTarget.value

      propsOnChange(newValue)
      validateAge(newValue)
    },
    [
      propsOnChange,
      validateAge,
    ],
  )

  const updateDateInputValue = useCallback(
    (
      input: HTMLInputElement,
      newValue: string,
      caretPosition: number,
    ) => {
      Object.getOwnPropertyDescriptor(
        HTMLInputElement.prototype,
        "value",
      )?.set?.call(
        input,
        newValue,
      )
      input.setSelectionRange(
        caretPosition,
        caretPosition,
      )

      // This value was set programmatically, so notify the controlled parent
      // directly instead of dispatching another input event.
      propsOnChange(newValue)
      validateAge(newValue)
    },
    [
      propsOnChange,
      validateAge,
    ],
  )

  const applyTypedDateSeparator = useCallback(
    (input: HTMLInputElement, separator: string): boolean => {
      if (
        separator.trim().length === 0
        || !dateSeparator.includes(separator)
      ) {
        return false
      }

      const caretPosition = input.selectionStart ?? input.value.length
      const fieldStart = input.value.lastIndexOf(
        dateSeparator,
        caretPosition - 1,
      ) + dateSeparator.length
      const currentField = input.value.slice(
        fieldStart,
        caretPosition,
      )

      if (!/^\d$/.test(currentField)) {
        return false
      }

      const paddedValue = [
        input.value.slice(
          0,
          fieldStart,
        ),
        `0${currentField}`,
        dateSeparator,
        input.value.slice(caretPosition),
      ].join("")
      const nextCaretPosition = caretPosition + dateSeparator.length + 1

      updateDateInputValue(
        input,
        paddedValue,
        nextCaretPosition,
      )

      return true
    },
    [
      dateSeparator,
      updateDateInputValue,
    ],
  )

  const moveCursorToEnd = useCallback(
    (input: HTMLInputElement) => {
      const end = input.value.length

      input.setSelectionRange(
        end,
        end,
      )
    },
    [],
  )

  const onDateFocus: FocusEventHandler<HTMLInputElement> = useCallback(
    (focusEvent) => {
      moveCursorToEnd(focusEvent.currentTarget)
    },
    [moveCursorToEnd],
  )

  const onDateClick: MouseEventHandler<HTMLInputElement> = useCallback(
    (mouseEvent) => {
      moveCursorToEnd(mouseEvent.currentTarget)
    },
    [moveCursorToEnd],
  )

  /*
   * Soft keyboards, particularly on Android, do not reliably emit a
   * useful keydown event for punctuation. Listen to the native beforeinput
   * event in capture phase so a typed separator can be replaced with the
   * padded, locale-formatted value before the browser inserts it.
   *
   * The capture listener also enforces append/backspace-only entry when the
   * caret is moved into a completed date. The React onDateKeyDown handler below is
   * retained as a fallback for physical and desktop keyboards.
   */
  useEffect(
    () => {
      const input = inputRef.current

      if (!input) {
        return undefined
      }

      const onNativeBeforeInput = (nativeEvent: Event) => {
        const {
          data,
          inputType,
        } = nativeEvent as InputEvent
        const cursorIsAtEnd = input.selectionStart === input.value.length
          && input.selectionEnd === input.value.length

        if (!cursorIsAtEnd) {
          moveCursorToEnd(input)

          if (inputType !== "deleteContentBackward") {
            nativeEvent.preventDefault()
          }

          return
        }

        if (data && applyTypedDateSeparator(
          input,
          data,
        )) {
          nativeEvent.preventDefault()
        }
      }

      input.addEventListener(
        "beforeinput",
        onNativeBeforeInput,
        { capture: true },
      )

      return () => input.removeEventListener(
        "beforeinput",
        onNativeBeforeInput,
        { capture: true },
      )
    },
    [
      applyTypedDateSeparator,
      moveCursorToEnd,
    ],
  )

  const onDateKeyDown: KeyboardEventHandler<HTMLInputElement> = useCallback(
    (keyboardEvent) => {
      const input = keyboardEvent.currentTarget
      const cursorIsAtEnd = input.selectionStart === input.value.length
        && input.selectionEnd === input.value.length

      // Ignore directional keys.  Its not practical to allow mid-field edits - they just don't work reliably.
      if ([
        "ArrowLeft",
        "ArrowRight",
        "Home",
        "End",
      ].includes(keyboardEvent.key)) {
        moveCursorToEnd(input)
        keyboardEvent.preventDefault()
        return
      }

      if (!cursorIsAtEnd) {
        moveCursorToEnd(input)

        if (keyboardEvent.key !== "Backspace") {
          keyboardEvent.preventDefault()
        }

        return
      }

      if (applyTypedDateSeparator(
        input,
        keyboardEvent.key,
      )) {
        keyboardEvent.preventDefault()
        return
      }
    },
    [
      applyTypedDateSeparator,
      moveCursorToEnd,
    ],
  )

  /*
   * show the warning dialog if the user has picked date that would make them
   * less than 18 years old
   */
  useEffect(
    () => {
      const date = getDateFromLocaleDateString(
        polyglot.locale(),
        value,
      )
      const isValidDate = !isNaN(date.valueOf())

      if (!value || !hasBlurred || !isValidDate) {
        return
      }

      const isTooYoung = dayjs(date)
        .startOf("day")
        .isAfter(latestValidDateOfBirth)

      const isInFuture = dayjs(date).isAfter(today)

      if (isTooYoung && !isInFuture) {
        setIsDialogOpen(true)
      }
    },
    [
      today,
      latestValidDateOfBirth,
      value,
      hasBlurred,
      polyglot,
    ],
  )

  const onBlur = useCallback(
    () => {
      const textInput = inputRef.current

      if (textInput) {
        const fieldWidths = maskitoDateMode.split("/").map(
          (field) => field.length,
        )
        const dateFields = textInput.value.split(dateSeparator)
        const completeDate = dateFields.length === fieldWidths.length
          && dateFields.every((field) => /^\d+$/.test(field))

        if (completeDate) {
          const normalizedValue = dateFields.map(
            (field, index) => field.padStart(
              fieldWidths[index],
              "0",
            ),
          ).join(dateSeparator)

          if (normalizedValue !== textInput.value) {
            propsOnChange(normalizedValue)
            validateAge(normalizedValue)
          }
        }
      }

      setHasBlurred(true)
    },
    [
      dateSeparator,
      maskitoDateMode,
      propsOnChange,
      validateAge,
      setHasBlurred,
    ],
  )

  const {
    dateFormat,
    dateFormatLength,
  } = useMemo(
    () => getLocaleDateFormat({
      language: polyglot.locale(),
      dayAbbreviation: polyglot.t(`${polyglotPrefix}day_abbreviation`),
      monthAbbreviation: polyglot.t(`${polyglotPrefix}month_abbreviation`),
      yearAbbreviation: polyglot.t(`${polyglotPrefix}year_abbreviation`),
    }),
    [polyglot],
  )

  const shouldApplyFormatConstraints = hasBlurred
    || value.length === dateFormatLength

  const dateInputRef = useRef<HTMLInputElement>(null)

  const openDatePicker: MouseEventHandler<HTMLButtonElement> = useCallback(
    () => {
      const dateInput = dateInputRef.current

      if (!dateInput) {
        return
      }

      // on iOS the picker will only show if the input is focused first
      dateInput.focus()

      if (typeof dateInput.showPicker === "function") {
        try {
          // Keep the modern behavior when supported.
          dateInput.showPicker()
          return
        } catch {
          // Some older WebKit builds expose inconsistent behavior.
        }
      }

      // Older iOS Safari does not support showPicker(), but click opens date UI.
      dateInput.click()
    },
    [dateInputRef],
  )

  // this controls the value of the date input
  const [
    dateInputValue,
    setDateInputValue,
  ] = useState(dayjs().format(dateInputFormat))

  // when the dateOfBirth value is a valid date, update the date input value
  useEffect(
    () => {
      const date = getDateFromLocaleDateString(
        polyglot.locale(),
        value,
      )
      const isValidDate = !isNaN(date.valueOf())

      if (isValidDate && dateInputRef.current) {
        setDateInputValue(dayjs(date).format(dateInputFormat))
      }
    },
    [
      value,
      setDateInputValue,
      polyglot,
    ],
  )

  const onDateInputChange: ChangeEventHandler<HTMLInputElement> = useCallback(
    (ev) => {
      const newValue = ev.target.value

      /*
       * constructs a dayjs instance then converts to date because it handles
       * time zones better than new Date(value)
       */
      const date = dayjs(newValue).toDate()
      const dateString = getLocaleDateStringFromDate(
        polyglot.locale(),
        date,
      )

      propsOnChange(dateString)

      validateAge(dateString)

      /*
       * focusing and immediately blurring the input element forces it to
       * re-evaluate its validity, see the ValidationContext
       */
      inputRef.current?.focus()
      inputRef.current?.blur()
    },
    [
      propsOnChange,
      validateAge,
      inputRef,
      polyglot,
    ],
  )

  const invalidErrorMessage = polyglot.t(
    `${polyglotPrefix}pattern_mismatch`,
    { dateFormat },
  )

  return (
    <>
      <ValidationContainer
        validationMode={hadValueOnFirstRender.current ? "immediate" : "onFirstInteraction"}
      >
        <LargeInputWithIconButton>
          <UnstyledInput
            type="date"
            autoComplete="bday"
            tabIndex={-1}
            ref={dateInputRef}
            className={customStyles.dateInput}
            onChange={onDateInputChange}
            min={dayjs(earliestValidDateOfBirth).format(dateInputFormat)}
            max={dayjs(latestValidDateOfBirth).format(dateInputFormat)}
            value={dateInputValue}
          />
          <RetriggerValidation
            updateCustomErrorCallback={validateAge}
            dependency={latestValidDateOfBirth.toISOString()}
            inputRef={inputRef}
          />
          <LargeInput
            ref={setTextInputRef}
            id={dateOfBirthId}
            placeholder={polyglot.t(
              `${polyglotPrefix}placeholder`,
              { dateFormat },
            )}
            value={value}
            className={styles.input}
            type="text"
            autoComplete="bday"
            required
            minLength={shouldApplyFormatConstraints ? dateFormatLength : undefined}
            maxLength={dateFormatLength}
            pattern={shouldApplyFormatConstraints ? inputPattern : undefined}
            onInput={onDateTextInput}
            onFocus={onDateFocus}
            onClick={onDateClick}
            onKeyDown={onDateKeyDown}
            onBlur={onBlur}
          />
          <LargeInputIconButton
            placementPreset="end"
            onClick={openDatePicker}
          >
            <CalendarOutlineIcon />
          </LargeInputIconButton>
        </LargeInputWithIconButton>
        <ValidationMessage
          id={dateOfBirthErrorId}
          invalidMessage={{
            patternMismatch: invalidErrorMessage,
            tooShort: invalidErrorMessage,
            tooLong: invalidErrorMessage,
            customError,
          }}
        />
      </ValidationContainer>
      <Dialog
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
      >
        <DialogContent
          id="age-limit-dialog"
        >
          <DialogHeading>
            {polyglot.t(`${polyglotPrefix}dialog.heading`)}
            <DialogCloseX />
          </DialogHeading>
          <DialogDescription>
            {polyglot.t(
              `${polyglotPrefix}dialog.description`,
              minimumAge,
            )}
          </DialogDescription>
          <DialogButton
            id="age-limit-dialog-btn"
          >
            {polyglot.t(`${polyglotPrefix}dialog.confirm_cta`)}
          </DialogButton>
        </DialogContent>
      </Dialog>
    </>
  )
}
